import axios from 'axios';

// One shared poll for everything that shows unread state: the two topbar
// dropdowns and the sidebar badges. Both dropdowns used to fetch once on mount
// and never again; now one pair of requests every 60 seconds (paused while the
// tab is hidden) feeds every subscriber.
const INTERVAL = 60000;
const listeners = new Set();
let timer = null;
let state = {
  messages: [],
  unreadMessages: 0,
  notifications: [],
  unreadNotifications: 0,
  // What arrived since the notification center was last opened, by kind
  // (reply, lead, message, deck, visit, activity): the label next to the bell.
  unseenByKind: {},
  // Replies received so far, split by answer: the results bar of the top bar.
  // Null until the first poll answers, so nothing flashes at zero.
  replies: null,
};

const emit = () => listeners.forEach(listener => listener(state));

// The bell polls the notification center preview: its five latest entries
// (community and campaigns merged) and how many arrived since the center was
// last opened.
export const refreshUnread = () => Promise.all([
  axios.get('conversations/latest').catch(() => null),
  axios.get('conversations/notifications/preview').catch(() => null),
]).then(([mail, notifs]) => {
  const next = { ...state };
  if (mail && mail.data && Object.prototype.hasOwnProperty.call(mail.data, 'conversations')) {
    next.messages = mail.data.conversations || [];
    next.unreadMessages = Number(mail.data.unread) || 0;
  }
  if (notifs && notifs.data && Object.prototype.hasOwnProperty.call(notifs.data, 'items')) {
    next.notifications = notifs.data.items || [];
    next.unreadNotifications = Number(notifs.data.unseen) || 0;
    next.unseenByKind = notifs.data.unseenByKind || {};
    next.replies = notifs.data.replies || null;
  }
  state = next;
  emit();
  return state;
});

const start = () => {
  if (timer) return;
  timer = setInterval(() => {
    if (!document.hidden) refreshUnread();
  }, INTERVAL);
  refreshUnread();
};

const stopIfIdle = () => {
  if (listeners.size === 0 && timer) {
    clearInterval(timer);
    timer = null;
  }
};

// Returns the unsubscribe function. The listener gets the current state at once.
export const subscribeUnread = (listener) => {
  listeners.add(listener);
  listener(state);
  start();
  return () => {
    listeners.delete(listener);
    stopIfIdle();
  };
};

// The user just opened the list: drop its badge locally without waiting for the next poll.
export const clearUnread = (kind) => {
  state = kind === 'messages'
    ? { ...state, unreadMessages: 0 }
    : { ...state, unreadNotifications: 0, unseenByKind: {} };
  emit();
};

// Two digits at most on a badge: the mail count is capped at 10 by the server
// (ConversationsRepository::UNREAD_CAP), the notifications count is not.
export const formatUnread = count => (count >= 10 ? '9+' : String(count));
